<template>
  <div class="group-type-picker">
    <div class="grid grid-cols-1 small-grid-cols-2 no-padding-horizontal">
      <f7-card raised 
        v-for="t in typesForCategory"
        :key="t.code"
        class="group-type-card padding margin-half"
        :class="modelValue === t.code ? 'selected' : 'unselected'"
        @click="select(t.code)"
        tabindex="0"
        @keydown.enter="select(t.code)"
        @keydown.space.prevent="select(t.code)"
        role="radio"
        :aria-checked="modelValue === t.code"
      >
        <f7-card-content :padding="false" >
          <f7-icon
            :f7="typeIcon(t.code)"
            size="28"
            class="type-icon"
          />
          <span class="margin-left font-label-md font-semibold text-align-center">{{ $t(t.label) }}</span>
        </f7-card-content>
      </f7-card>
    </div>

    <p class="group-type-hint font-body-sm text-on-surface-variant px-1">
      {{ $t('group.creationPrimaryTypeHint') }}
    </p>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { GROUP_TYPES_BY_CATEGORY } from '../../constants/group-taxonomy.js'

const props = defineProps({
  category: { type: String, default: null },
  modelValue: { type: String, default: null },
})
const emit = defineEmits(['update:modelValue'])

const typesForCategory = computed(() =>
  props.category ? (GROUP_TYPES_BY_CATEGORY[props.category] ?? []) : [],
)

// All icons verified against Framework7 Icons font (https://framework7.io/icons/)
const typeIcon = (code) => {
  const icons = {
    NUCLEAR_FAMILY: 'person_2_fill',
    EXTENDED_FAMILY: 'person_3_fill',
    CLOSE_FRIENDS: 'heart_fill',
    NEIGHBOURS: 'house_fill',
    FLATMATES: 'bed_double_fill',
    CLASSMATES: 'person_2_fill',
    ALUMNI_PROMOTION: 'graduationcap_fill',
    ALUMNI_ASSOCIATION: 'building_2_fill',
    STUDY_GROUP: 'book_fill',
    PROFESSIONAL_TRAINING: 'briefcase_fill',
    CHURCH_PARISH: 'building_2_fill',
    MOSQUE_MUSLIM_COMMUNITY: 'moon_stars',
    OTHER_FAITH: 'sparkles',
    CHOIR_PRAISE: 'music_note',
    PRAYER_FELLOWSHIP: 'hand_raised',
    RELIGIOUS_YOUTH: 'person_2_fill',
    ETHNIC_COMMUNITY: 'globe',
    REGIONAL_COMMUNITY: 'map_fill',
    DIASPORA: 'airplane',
    EXPATS: 'location',
    CULTURAL_ASSOCIATION: 'theatermasks',
    COLLEAGUES: 'person_3_fill',
    TRADERS_INFORMAL: 'cart_fill',
    PROFESSIONALS_FORMAL: 'briefcase_fill',
    FARMERS_COOPERATIVE: 'leaf_arrow_circlepath',
    ENTREPRENEURS: 'lightbulb_fill',
    UNION_PROFESSIONAL_ORDER: 'shield_fill',
    ADVERTISERS_BRANDS: 'megaphone_fill',
    SPORTS_TEAM: 'sportscourt',
    SPORTS_CLUB_FITNESS: 'dumbbell',
    GAMING_ESPORT: 'game_controller_fill',
    MUSIC_ARTS: 'music_note_fill',
    WELLNESS_HEALTH: 'heart_fill',
    READING_CULTURE: 'book_fill',
    CREATIVE_CONTENT: 'paintbrush_fill',
    MUTUAL_AID_ASSOCIATION: 'hand_raised_fill',
    HEALTH_SUPPORT_GROUP: 'heart_fill',
    NGO_CIVIC: 'building_2_fill',
    CAUSE_ADVOCACY: 'megaphone_fill',
    NEIGHBOURHOOD_COMMITTEE: 'house_fill',
    TONTINE_MUTUAL: 'arrow_2_circlepath',
  }
  return icons[code] ?? 'circle_fill'
}

function select(code) {
  emit('update:modelValue', props.modelValue === code ? null : code)
}
</script>

<style scoped>
.group-type-picker {
  display: flex;
  flex-direction: column;
  /* gap: 16px; */
}

.group-type-card {
  border-radius: 16px;
  background: var(--f7-card-bg-color, #fff);
  color: var(--f7-on-surface, #161d18);
  border: 1px solid var(--f7-list-item-border-color, rgba(0,0,0,0.08));
  transition: background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease, transform 0.1s ease;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  cursor: pointer;
  /* min-height: 100px; */
}

.group-type-card .type-icon {
  color: var(--f7-on-surface-variant, #6f7a71);
}

.group-type-card:focus-visible {
  outline: 2px solid var(--f7-theme-color, #1a6b42);
  outline-offset: 2px;
}

.group-type-card.unselected:hover {
  background: var(--f7-surface-container, #f8f9fa);
  border-color: var(--avocash-outline, rgba(0,0,0,0.12));
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
}

.group-type-card.selected {
  background: color-mix(in srgb, var(--f7-theme-color) 14%, var(--f7-card-bg-color, #fff));
  border-color: var(--f7-theme-color);
  box-shadow: 0 2px 8px color-mix(in srgb, var(--f7-theme-color) 20%, transparent);
}

.group-type-card.selected .type-icon {
  color: var(--f7-theme-color);
}

.group-type-card.unselected:active {
  transform: scale(0.98);
}

.card-inner {
  width: 100%;
}

.group-type-hint {
  margin-top: 12px;
  /* text-align: center; */
}
</style>